import { useTranslation } from 'react-i18next';

import { useMutation } from '@tanstack/react-query';

import toast from '~/utils/toast';

type OrderReceiptResponse = { receipt_url?: string | null };

const isOrderReceiptResponse = (value: unknown): value is OrderReceiptResponse =>
  typeof value === 'object' && value !== null;

interface ReturnProps {
  /** Fetch the receipt for an order and open it in a new tab. */
  viewReceipt: (orderId: string) => void;
  /** Order whose receipt is mid-fetch (the in-flight mutation variable) — drives the per-row spinner. */
  pendingReceiptOrderId: string | null;
}

/**
 * Fetches a Stripe receipt URL for an order and opens it in a new tab. The `/order_receipt` action
 * always resolves 200 — a failure comes back as `{ receipt_url: null }` — so success keys off a
 * truthy `receipt_url`, not the HTTP status. The in-flight order id comes from the mutation's own
 * `variables`, so no separate pending-state is needed.
 */
const useOrderReceipt = (): ReturnProps => {
  const { t } = useTranslation();

  const { mutate, isPending, variables } = useMutation({
    mutationFn: async (orderId: string): Promise<string> => {
      const response = await fetch('/order_receipt', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order_id: orderId }),
      });
      const data: unknown = await response.json().catch(() => null);
      if (!response.ok || !isOrderReceiptResponse(data) || !data.receipt_url) {
        throw new Error('Failed to fetch receipt');
      }
      return data.receipt_url;
    },
    onSuccess: (url) => {
      window.open(url, '_blank', 'noopener,noreferrer');
    },
    onError: () => {
      toast.error(t('manage.orders.view_receipt_error', { defaultValue: 'Failed to fetch receipt' }));
    },
  });

  return {
    viewReceipt: mutate,
    pendingReceiptOrderId: isPending ? (variables ?? null) : null,
  };
};

export default useOrderReceipt;
